import type { Priority } from "@app/core";
import { useEffect, useRef, useState } from "react";
import { inkDebug, type InkPulse } from "./ink-sim.js";
import { PRIORITY_PAPER } from "./palette.js";
import type { RailTopic } from "./TopicRail.js";

interface Deps {
  topicId: string | null;
  topicIndex: number;
  /** Wash color of the (new) topic selection. */
  topicColor: string;
  priority: Priority;
  priorityColor: string;
  /** Raw custom color key (null = none). */
  customKey: string | null;
  /** Wash color of the custom selection. */
  customColor: string;
  subSel: string[];
  rail: RailTopic[];
}

/** Directional ink pulses: figure out *which* control changed and emit a pulse
 *  telling the ink canvas which edge to inject from (topic rail = left,
 *  priority rail = top, custom swatches = bottom-right corner), where along
 *  that edge, and the new color. Color-REMOVAL selections (topic deselect,
 *  priority → none, custom → No color) inject nothing — a neutral gray rinse
 *  was tried and read as dirty water. Switching a subtopic ON fires a small
 *  puff at the subtopic's real vertical position. */
export function useInkPulse(d: Deps): InkPulse | null {
  const [pulse, setPulse] = useState<InkPulse | null>(null);
  const prevSel = useRef({ topicId: d.topicId, priority: d.priority, customKey: d.customKey });
  const pulseId = useRef(0);
  // The dialog hydrates the form in an effect right after the card mounts —
  // those programmatic changes must not fire a phantom bloom (it also eats a
  // third of the particle budget before the first real click). Changes inside
  // the arm window only sync prevSel.
  const armAt = useRef(0);
  if (armAt.current === 0) armAt.current = performance.now() + 200;
  // Pulse counter as of this render — lets the subtopic effect detect a big
  // pulse emitted earlier in the SAME commit (single pulse slot, batched).
  const idAtRender = useRef(0);
  idAtRender.current = pulseId.current;

  useEffect(() => {
    const p = prevSel.current;
    let changed: "topic" | "priority" | "custom" | null = null;
    if (d.topicId !== p.topicId) changed = "topic";
    else if (d.priority !== p.priority) changed = "priority";
    else if (d.customKey !== p.customKey) changed = "custom";
    prevSel.current = { topicId: d.topicId, priority: d.priority, customKey: d.customKey };
    if (!changed) return;
    if (performance.now() < armAt.current) {
      if (inkDebug()) console.info(`[ink] swallowed ${changed} change (hydration arm window)`);
      return;
    }
    if (inkDebug()) console.info(`[ink] emit ${changed}`);

    let next: Omit<InkPulse, "id">;
    if (changed === "topic") {
      if (!d.topicId) return; // deselect → nothing to inject
      const i = Math.max(0, d.topicIndex);
      next = { src: "left", ox: 0, oy: Math.min(0.95, 0.16 + i * 0.135), col: d.topicColor };
    } else if (changed === "priority") {
      if (d.priority === "none") return;
      const i = PRIORITY_PAPER.findIndex((x) => x.id === d.priority);
      next = { src: "top", ox: 0.14 + i * 0.11, oy: 0, col: d.priorityColor };
    } else {
      if (d.customKey === null) return; // "No color" → nothing to inject
      // Custom color is shot in from the lower-right corner, heading up-left.
      next = { src: "br", ox: 0.82, oy: 0.88, col: d.customColor };
    }
    pulseId.current += 1;
    setPulse({ id: pulseId.current, ...next });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d.topicId, d.priority, d.customKey]);

  // Tiny color release when a subtopic is switched ON — a miniature of the big
  // pulse, emitted at the subtopic's REAL vertical position over the card.
  const prevSubSel = useRef(d.subSel);
  useEffect(() => {
    const prev = prevSubSel.current;
    const added = d.subSel.find((id) => !prev.includes(id));
    prevSubSel.current = d.subSel;
    if (!added || performance.now() < armAt.current) return;
    // A root switch can add a subtopic in the same commit as the big topic
    // pulse — the tiny puff must not replace it in the single pulse slot.
    if (pulseId.current !== idAtRender.current) return;
    const t = d.rail.find((r) => r.children.some((s) => s.id === added));
    if (!t) return;
    let oy = 0.5;
    const el = document.querySelector(`.subtopic-tab[data-sub-id="${added}"]`);
    const card = el?.closest(".card-wrap")?.querySelector(".card") ?? document.querySelector(".paper-form .card");
    if (el && card) {
      const er = el.getBoundingClientRect();
      const cr = card.getBoundingClientRect();
      if (cr.height > 0) oy = Math.max(0.03, Math.min(0.97, (er.top + er.height / 2 - cr.top) / cr.height));
    }
    pulseId.current += 1;
    setPulse({ id: pulseId.current, src: "left", ox: 0, oy, col: t.color, amount: 0.12, spread: 0.045 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d.subSel]);

  return pulse;
}
